import * as React from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  useGlobalSearchQuery,
  type SearchResult,
  type SearchResultType,
} from "@/slices/search/searchApi";

/* ── Icons (thin-stroke, Lucide-style; matches prototype icons.jsx) ──────── */

interface IconProps {
  size?: number;
  sw?: number;
  className?: string;
}

function SearchIcon({ size = 19, sw = 2, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ContactIcon({ size = 18, sw = 1.9, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M16 18a4 4 0 0 0-8 0" />
      <circle cx="12" cy="11" r="3" />
      <rect x="3" y="4" width="18" height="18" rx="2" />
    </svg>
  );
}

function BriefcaseIcon({ size = 18, sw = 1.9, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}

function BuildingIcon({ size = 18, sw = 1.9, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4M10 10h4M10 14h4" />
    </svg>
  );
}

function CornerDownIcon({ size = 15, sw = 2, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polyline points="9 10 4 15 9 20" />
      <path d="M20 4v7a4 4 0 0 1-4 4H4" />
    </svg>
  );
}

/* ── Entity config ───────────────────────────────────────────────────────── */

const ENTITY: Record<
  SearchResultType,
  {
    label: string;
    Icon: (p: IconProps) => React.ReactElement;
    /** Tinted icon tile: [background, foreground]. */
    tile: string;
  }
> = {
  person: { label: "Person", Icon: ContactIcon, tile: "bg-[#EEF0FB] text-[#181EAE]" },
  deal: { label: "Deal", Icon: BriefcaseIcon, tile: "bg-[#ECF6F0] text-[#2E7A52]" },
  property: { label: "Property", Icon: BuildingIcon, tile: "bg-[#F0EDFB] text-[#6D28D9]" },
};

/** Scoped enter animations (matches prototype .cmd-scrim / .cmd keyframes). */
const GS_KEYFRAMES = `
@keyframes gs-scrim-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes gs-cmd-in { from { transform: translateY(-10px) scale(.985); opacity: .5; } to { transform: none; opacity: 1; } }
.gs-scrim { animation: gs-scrim-in .15s cubic-bezier(.2,0,0,1); }
.gs-panel { animation: gs-cmd-in .18s cubic-bezier(.2,0,0,1); }
@media (max-width: 880px) { .gs-panel { animation: gs-scrim-in .15s cubic-bezier(.2,0,0,1); } }
`;

const GROUP_ORDER: SearchResultType[] = ["person", "deal", "property"];
const GROUP_LABEL: Record<SearchResultType, string> = {
  person: "People",
  deal: "Deals",
  property: "Properties",
};

/* ── Debounced value hook ────────────────────────────────────────────────── */

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/* ── Overlay ─────────────────────────────────────────────────────────────── */

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

export function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const navigate = useNavigate();
  const [q, setQ] = React.useState("");
  const [sel, setSel] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Reset + focus on open.
  React.useEffect(() => {
    if (!open) return;
    setQ("");
    setSel(0);
    const id = setTimeout(() => inputRef.current?.focus(), 40);
    return () => clearTimeout(id);
  }, [open]);

  const typed = q.trim();
  const debouncedQ = useDebounced(typed, 150);

  const { currentData, isFetching } = useGlobalSearchQuery(
    { q: debouncedQ },
    { skip: !open || debouncedQ.length === 0 }
  );

  // Flatten grouped results in People → Deals → Properties order for keyboard
  // traversal. Use `currentData` (not `data`) so results clear the instant the
  // query arg changes — we never show stale matches from a previous query.
  const flat = React.useMemo<SearchResult[]>(() => {
    const results = currentData ?? [];
    return GROUP_ORDER.flatMap((type) =>
      results.filter((r) => r.type === type)
    );
  }, [currentData]);

  // Keep selection in range as results change.
  React.useEffect(() => {
    setSel((s) => (s >= flat.length ? Math.max(0, flat.length - 1) : s));
  }, [flat.length]);

  const go = React.useCallback(
    (result: SearchResult | undefined) => {
      if (!result) return;
      onClose();
      navigate(result.href);
    },
    [navigate, onClose]
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(flat.length - 1, s + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(0, s - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[sel]);
    }
  };

  if (!open) return null;

  const hasQuery = typed.length > 0;
  // `pending` = the debounce hasn't caught up to what's typed (so any results
  // on screen belong to an older query). `loading` drives the input spinner;
  // `searching` swaps the body to a loading state so stale rows are never shown
  // under a newer query.
  const pending = hasQuery && typed !== debouncedQ;
  const loading = pending || (hasQuery && isFetching);
  const searching = pending || (isFetching && flat.length === 0);

  // Render grouped while tracking a running flat index for selection.
  let runningIdx = -1;

  const overlay = (
    <div
      className="gs-scrim fixed inset-0 z-[200] flex items-start justify-center bg-[rgb(16_18_24_/_0.36)] px-5 pt-[11vh] pb-6 backdrop-blur-[3px] max-[880px]:items-stretch max-[880px]:p-0 max-[880px]:backdrop-blur-none"
      onMouseDown={onClose}
    >
      <style>{GS_KEYFRAMES}</style>
      <div
        role="dialog"
        aria-label="Search"
        onMouseDown={(e) => e.stopPropagation()}
        className="gs-panel flex w-full max-w-[624px] max-h-[74vh] flex-col overflow-hidden rounded-2xl border border-border-default bg-surface shadow-[0_16px_40px_-8px_rgb(16_16_29_/_0.16),0_4px_12px_-4px_rgb(16_16_29_/_0.08)] max-[880px]:h-[100dvh] max-[880px]:max-h-none max-[880px]:max-w-none max-[880px]:rounded-none max-[880px]:border-0 max-[880px]:shadow-none"
      >
        {/* Input row */}
        <div className="flex items-center gap-[11px] border-b border-border-subtle px-[17px] py-[15px] max-[880px]:p-[14px]">
          <SearchIcon className="shrink-0 text-ink-faint" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Search people, deals, properties…"
            aria-label="Search"
            className="min-w-0 flex-1 border-none bg-transparent text-[16px] tracking-[-0.01em] text-ink-primary outline-none placeholder:text-ink-faint"
          />
          {loading && (
            <span
              aria-hidden
              className="size-[15px] shrink-0 animate-spin rounded-full border-2 border-ink-faint/25 border-t-ink-muted"
            />
          )}
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md border border-border-default bg-canvas px-[7px] py-[3px] text-[11px] font-semibold text-ink-faint hover:bg-hover hover:text-ink-muted max-[880px]:hidden"
          >
            Esc
          </button>
          <button
            type="button"
            onClick={onClose}
            className="hidden shrink-0 border-none bg-transparent px-[2px] py-1 text-[14px] font-semibold text-brand max-[880px]:inline-block"
          >
            Cancel
          </button>
        </div>

        {/* Results body */}
        <div className="overflow-y-auto p-[7px]">
          {!hasQuery ? (
            <div className="px-5 py-11 text-center text-[13px] text-ink-muted">
              Search across people, deals, and properties.
            </div>
          ) : searching ? (
            <div className="flex items-center justify-center gap-2 px-5 py-11 text-[13px] text-ink-muted">
              <span
                aria-hidden
                className="size-[14px] animate-spin rounded-full border-2 border-ink-faint/25 border-t-ink-muted"
              />
              Searching…
            </div>
          ) : flat.length === 0 ? (
            <div className="px-5 py-11 text-center text-[13px] text-ink-muted">
              No matches for “{debouncedQ}”.
            </div>
          ) : (
            GROUP_ORDER.map((type) => {
              const rows = flat.filter((r) => r.type === type);
              if (!rows.length) return null;
              const cfg = ENTITY[type];
              return (
                <div className="mb-[2px]" key={type}>
                  <div className="px-[11px] pb-[5px] pt-[9px] text-[10.5px] font-bold uppercase tracking-[0.07em] text-ink-faint">
                    {GROUP_LABEL[type]}
                  </div>
                  {rows.map((r) => {
                    runningIdx += 1;
                    const myIdx = runningIdx;
                    const selected = sel === myIdx;
                    return (
                      <button
                        type="button"
                        key={r.id}
                        aria-selected={selected}
                        onMouseEnter={() => setSel(myIdx)}
                        onClick={() => go(r)}
                        className={`flex w-full items-center gap-3 rounded-[10px] border-none bg-transparent px-[11px] py-2 text-left transition-colors max-[880px]:p-[11px] ${
                          selected ? "bg-hover" : ""
                        }`}
                      >
                        <span
                          className={`flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] ${cfg.tile}`}
                        >
                          <cfg.Icon size={18} sw={1.9} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-[13.5px] font-semibold text-ink-primary">
                            {r.name}
                          </span>
                          <span className="mt-px block overflow-hidden text-ellipsis whitespace-nowrap text-[12px] text-ink-muted">
                            {r.sub}
                          </span>
                        </span>
                        {selected ? (
                          <CornerDownIcon className="shrink-0 text-ink-faint" />
                        ) : (
                          <span className="shrink-0 rounded-full border border-border-default bg-canvas px-[9px] py-[3px] text-[10.5px] font-semibold text-ink-faint">
                            {cfg.label}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer hint (desktop only) */}
        <div className="flex items-center gap-4 border-t border-border-subtle px-4 py-[9px] text-[11.5px] text-ink-faint max-[880px]:hidden">
          <span>
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> navigate
          </span>
          <span>
            <Kbd>↵</Kbd> open
          </span>
          <span>
            <Kbd>esc</Kbd> close
          </span>
          <span className="ml-auto">
            {flat.length} result{flat.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="mr-[3px] rounded-[5px] border border-border-default bg-canvas px-[5px] py-px font-semibold text-ink-muted">
      {children}
    </kbd>
  );
}
